import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { PERSONA_AI_EDIT_IDS_MAX } from "@nexus/contracts";
import { Button, Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";

import type { PersonaDocumentExportFormat } from "@/features/persona-opportunities/lib/persona-script-docx";

import { DocumentExportMenu } from "../../../shared/DocumentExportMenu/DocumentExportMenu.js";

export interface PersonaScriptBulkActionBarProps {
  selectedScriptCount: number;
  selectedBriefCount?: number;
  onClearSelection: () => void;
  onExport: (format: PersonaDocumentExportFormat) => void;
  onAiEdit: () => void;
  onAiEditBriefs?: () => void;
  onSendToVendor: () => void;
  /** Platform tenant (Creative Team) only: adapt the selected scripts for other tenants. Hidden when omitted. */
  onBroadcast?: () => void;
  onDelete?: () => void;
  canAiEdit: boolean;
  canDelete?: boolean;
  isAiEditing: boolean;
  isDeleting?: boolean;
  isExporting?: boolean;
}

export function PersonaScriptBulkActionBar({
  selectedScriptCount,
  selectedBriefCount = 0,
  onClearSelection,
  onExport,
  onAiEdit,
  onAiEditBriefs,
  onSendToVendor,
  onBroadcast,
  onDelete,
  canAiEdit,
  canDelete = false,
  isAiEditing,
  isDeleting = false,
  isExporting = false,
}: PersonaScriptBulkActionBarProps) {
  const { t } = useTranslation();
  const selectedCount = selectedScriptCount + selectedBriefCount;

  if (selectedCount <= 0) return null;

  const scriptsLabel = t("personaOpportunities.scripts.bulk.selected", {
    defaultValue_one: "{{count}} script selected",
    defaultValue_other: "{{count}} scripts selected",
    count: selectedScriptCount,
  });
  const briefsLabel = t("personaOpportunities.scripts.briefs.bulk.selected", {
    defaultValue_one: "{{count}} brief selected",
    defaultValue_other: "{{count}} briefs selected",
    count: selectedBriefCount,
  });
  const selectedLabel =
    selectedScriptCount > 0 && selectedBriefCount > 0
      ? t("personaOpportunities.scripts.bulk.selectedMixed", {
          defaultValue: "{{scripts}} · {{briefs}}",
          scripts: scriptsLabel,
          briefs: briefsLabel,
        })
      : selectedBriefCount > 0
        ? briefsLabel
        : scriptsLabel;

  const showDualAi = selectedScriptCount > 0 && selectedBriefCount > 0;
  const scriptsOverCap = selectedScriptCount > PERSONA_AI_EDIT_IDS_MAX;
  const briefsOverCap = selectedBriefCount > PERSONA_AI_EDIT_IDS_MAX;
  const scriptsLimitWarning = t("personaOpportunities.scripts.bulk.aiEditLimitScripts", {
    defaultValue: "AI Edit is limited to {{max}} scripts per job. Deselect some to continue.",
    max: PERSONA_AI_EDIT_IDS_MAX,
  });
  const briefsLimitWarning = t("personaOpportunities.scripts.briefs.bulk.aiEditLimitBriefs", {
    defaultValue: "AI Edit is limited to {{max}} briefs per job. Deselect some to continue.",
    max: PERSONA_AI_EDIT_IDS_MAX,
  });
  const bothLimitWarning = t("personaOpportunities.scripts.bulk.aiEditLimitBoth", {
    defaultValue:
      "AI Edit is limited to {{max}} scripts and {{max}} briefs per job. Deselect some to continue.",
    max: PERSONA_AI_EDIT_IDS_MAX,
  });
  const overCapWarning = scriptsOverCap && briefsOverCap
    ? bothLimitWarning
    : scriptsOverCap
      ? scriptsLimitWarning
      : briefsOverCap
        ? briefsLimitWarning
        : null;
  const singleKindOverCap = selectedBriefCount > 0 ? briefsOverCap : scriptsOverCap;
  const singleKindWarning = selectedBriefCount > 0 ? briefsLimitWarning : scriptsLimitWarning;
  // Only scripts can be broadcast; a brief in the selection hides the action.
  const showBroadcast = Boolean(onBroadcast) && selectedScriptCount > 0 && selectedBriefCount === 0;

  const bar = (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-300 motion-reduce:animate-none"
      role="region"
      aria-label={t("personaOpportunities.scripts.bulk.barAria", {
        defaultValue_one: "Bulk actions for {{count}} selected script",
        defaultValue_other: "Bulk actions for {{count}} selected scripts",
        count: selectedCount,
      })}
      data-testid="persona-script-bulk-action-bar"
    >
      <div className="pointer-events-auto relative w-full max-w-[1680px] px-8">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-8 bottom-full h-10 bg-gradient-to-t from-background to-transparent"
        />
        <div className="border-t border-border bg-background px-4 py-4 shadow-[0_-1px_3px_0_rgba(0,0,0,0.1),0_-1px_2px_-1px_rgba(0,0,0,0.1)] sm:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-foreground">{selectedLabel}</span>
            <button
              type="button"
              onClick={onClearSelection}
              className="text-sm text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {t("personaOpportunities.scripts.bulk.clear", "Clear")}
            </button>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              {canDelete && onDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  iconStart="trash-2"
                  disabled={isDeleting}
                  onClick={onDelete}
                  data-testid="persona-script-bulk-delete"
                >
                  {t("personaOpportunities.scripts.bulk.delete", "Delete")}
                </Button>
              ) : null}
              <DocumentExportMenu
                onExport={onExport}
                busy={isExporting}
                testId="persona-script-bulk-export"
              />
              {canAiEdit ? (
                <TooltipProvider delayDuration={200}>
                  {showDualAi ? (
                    <>
                      <AiEditButton
                        isAiEditing={isAiEditing}
                        overCap={scriptsOverCap}
                        overCapReason={scriptsLimitWarning}
                        onClick={onAiEdit}
                        label={t(
                          "personaOpportunities.scripts.bulk.aiEditScripts",
                          "AI Edit scripts",
                        )}
                        testId="persona-script-bulk-ai-scripts"
                      />
                      <AiEditButton
                        isAiEditing={isAiEditing}
                        overCap={briefsOverCap}
                        overCapReason={briefsLimitWarning}
                        onClick={onAiEditBriefs ?? onAiEdit}
                        label={t(
                          "personaOpportunities.scripts.briefs.bulk.aiEditBriefs",
                          "AI Edit briefs",
                        )}
                        testId="persona-script-bulk-ai-briefs"
                      />
                    </>
                  ) : (
                    <AiEditButton
                      isAiEditing={isAiEditing}
                      overCap={singleKindOverCap}
                      overCapReason={singleKindWarning}
                      onClick={selectedBriefCount > 0 ? (onAiEditBriefs ?? onAiEdit) : onAiEdit}
                      label={t("personaOpportunities.scripts.actions.aiEdit", "AI Edit")}
                    />
                  )}
                </TooltipProvider>
              ) : null}
              {showBroadcast ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={onBroadcast}
                  data-testid="persona-script-bulk-broadcast"
                >
                  {t("personaOpportunities.scripts.bulk.broadcast", "Generate for tenants")}
                </Button>
              ) : null}
              <Button type="button" size="sm" onClick={onSendToVendor}>
                {t(
                  "personaOpportunities.scripts.actions.sendToVendor",
                  "Send to production partner",
                )}
              </Button>
            </div>
          </div>
          {overCapWarning ? (
            <p
              role="status"
              className="mt-2 text-sm text-muted-foreground"
              data-testid="persona-script-bulk-ai-limit-warning"
            >
              {overCapWarning}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(bar, document.body);
}

function AiEditButton({
  isAiEditing,
  overCap,
  overCapReason,
  onClick,
  label,
  testId,
}: {
  isAiEditing: boolean;
  overCap: boolean;
  overCapReason: string;
  onClick: () => void;
  label: string;
  testId?: string;
}) {
  const disabled = isAiEditing || overCap;
  const button = (
    <Button
      type="button"
      variant="ai"
      size="sm"
      iconStart={isAiEditing ? undefined : "sparkles"}
      className="border border-ai"
      disabled={disabled}
      aria-busy={isAiEditing}
      aria-disabled={disabled || undefined}
      aria-label={overCap && !isAiEditing ? `${label}. ${overCapReason}` : undefined}
      onClick={onClick}
      data-testid={testId}
    >
      {isAiEditing ? (
        <>
          <Icon
            name="loader"
            size="sm"
            color="inherit"
            className="animate-spin motion-reduce:animate-none"
            aria-hidden
          />
          {label}
        </>
      ) : (
        label
      )}
    </Button>
  );

  if (!overCap || isAiEditing) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{button}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{overCapReason}</TooltipContent>
    </Tooltip>
  );
}
